import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=unknown, false=guest, obj=user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applySession = (data) => {
    if (data?.token) {
      localStorage.setItem("cc_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user, mfa_required: false };
    }
    if (data?.mfa_required) {
      return {
        ok: true,
        mfa_required: true,
        mfa_token: data.mfa_token,
        user: data.user,
      };
    }
    if (data?.verification_required) {
      return {
        ok: true,
        verification_required: true,
        user: data.user,
      };
    }
    if (data?.user) {
      setUser(data.user);
      return { ok: true, user: data.user };
    }
    return { ok: true };
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return applySession(data);
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const completeMfa = async (mfaToken, code) => {
    try {
      const { data } = await api.post("/auth/login/mfa", { mfa_token: mfaToken, code });
      return applySession(data);
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      return applySession(data);
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const verifyEmail = async (token) => {
    try {
      const { data } = await api.post("/auth/verify-email", { token });
      return applySession(data);
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const consumeMagicLink = async (token) => {
    try {
      const { data } = await api.post("/auth/magic-link/consume", { token });
      return applySession(data);
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("cc_token");
    setUser(false);
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refresh,
        completeMfa,
        verifyEmail,
        consumeMagicLink,
        applySession,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
